/**
 * TagsEditor - the Tags field in the agent Properties card
 * (plans/2026-07-13-ticket-tags.md).
 *
 * Chips + an inline combobox mirroring the CC line (RecipientCombobox
 * and RecipientHeader's RecipientChip; the chip is re-implemented
 * locally because RecipientChip is not exported and the composer's
 * component stays untouched). Two deliberate deviations from the CC
 * combobox (plan D9), both because an accidental tag write pollutes
 * the SHARED tag vocabulary instantly, while an accidental CC chip
 * stays local until Send:
 *   - blur closes the dropdown WITHOUT committing;
 *   - no comma / semicolon commit (Enter and click only).
 *
 * Suggestions come from the cached GET /agent/tags vocabulary filtered
 * client-side (case-insensitive substring, minus tags already on the
 * ticket). The list is expected to hold dozens of rows, so no server
 * search endpoint is needed.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";

import {
  useAddTicketTag,
  useAgentTags,
  useRemoveTicketTag,
  useTicketTags,
} from "@/hooks/useTicketTags";
import type { TagResponse } from "@/types/api";

export interface TagsEditorProps {
  ticketNumber: number;
}

export function TagsEditor({ ticketNumber }: TagsEditorProps): JSX.Element {
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimerRef = useRef<number | null>(null);

  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);

  const allTags = useAgentTags();
  const ticketTags = useTicketTags(ticketNumber);
  const addTag = useAddTicketTag(ticketNumber);
  const removeTag = useRemoveTicketTag(ticketNumber);
  const isPending = addTag.isPending || removeTag.isPending;

  const applied = useMemo(() => ticketTags.data ?? [], [ticketTags.data]);
  const appliedNames = useMemo(
    () => new Set(applied.map((t) => t.name.toLowerCase())),
    [applied],
  );

  // Client-side filter over the cached vocabulary: case-insensitive
  // substring on the typed text, minus tags already on the ticket so
  // an agent can't double-add from the dropdown.
  const needle = value.trim().toLowerCase();
  const items = useMemo(
    () =>
      (allTags.data ?? []).filter(
        (t) =>
          !appliedNames.has(t.name.toLowerCase()) &&
          (needle === "" || t.name.toLowerCase().includes(needle)),
      ),
    [allTags.data, appliedNames, needle],
  );

  // Reset highlight when results change (RecipientCombobox parity).
  useEffect(() => {
    setHighlightIndex(0);
  }, [items.length]);

  function commitName(name: string): void {
    // One in-flight add at a time. Guarding here instead of disabling
    // the input keeps focus in the field (a disabled element loses
    // focus in real browsers, which would break the add-another-
    // without-re-clicking flow) and collapses a double Enter or a
    // double-clicked suggestion into a single POST.
    if (addTag.isPending) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    // The dropdown already excludes applied tags, but the literal
    // Enter path can retype one; the backend would 409, so skip the
    // request instead of surfacing an error for a no-op.
    if (appliedNames.has(trimmed.toLowerCase())) {
      setValue("");
      return;
    }
    addTag.mutate({ name: trimmed });
    setValue("");
  }

  function commitSuggestion(tag: TagResponse): void {
    commitName(tag.name);
    setOpen(true);
    // Keep focus so the agent can add another without re-clicking.
    inputRef.current?.focus();
  }

  function handleEnter(e: React.KeyboardEvent<HTMLInputElement>): void {
    e.preventDefault();
    if (open && items.length > 0 && highlightIndex < items.length) {
      commitSuggestion(items[highlightIndex]!);
      return;
    }
    commitName(value);
  }

  return (
    <div data-testid="tags-editor">
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-neutral-700"
      >
        Tags
      </label>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-2 py-1.5 shadow-sm">
        {applied.map((tag) => (
          <span
            key={tag.id}
            data-testid="tag-chip"
            title={tag.name}
            className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700"
          >
            <span className="max-w-[18rem] truncate">{tag.name}</span>
            <button
              type="button"
              aria-label={`Remove ${tag.name}`}
              // Unicode multiplication sign, not FA fa-xmark - the icon
              // font reliably renders blank at chip size on this design
              // system (see the RecipientChip comment in
              // RecipientHeader.tsx).
              className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-sm leading-none text-neutral-500 hover:bg-neutral-200 hover:text-red-600 disabled:opacity-50"
              onClick={() => removeTag.mutate(tag.id)}
              disabled={isPending}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </span>
        ))}
        <div className="relative min-w-[8rem] flex-1">
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              open && items.length > 0
                ? `${listboxId}-${highlightIndex}`
                : undefined
            }
            aria-label="Add tag"
            placeholder={applied.length === 0 ? "Add tag" : ""}
            maxLength={100}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              // Delay close so a click on a dropdown row registers
              // before the input loses focus; the mousedown handler on
              // the listbox cancels this timer. Unlike the CC combobox
              // there is NO blur-commit (plan D9): clicking away must
              // never create a tag in the shared vocabulary.
              blurTimerRef.current = window.setTimeout(() => {
                setOpen(false);
              }, 120);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleEnter(e);
                return;
              }
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setOpen(true);
                setHighlightIndex((i) =>
                  items.length === 0 ? 0 : Math.min(i + 1, items.length - 1),
                );
                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightIndex((i) => Math.max(i - 1, 0));
                return;
              }
              if (e.key === "Escape") {
                setOpen(false);
                return;
              }
            }}
            className="w-full border-0 bg-transparent px-1 py-0.5 text-sm text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
            data-testid="tags-combobox-input"
          />
          {(addTag.isError || removeTag.isError) && (
            // A tag write commits server-side immediately (unlike a CC
            // chip, which is composer-local until Send), so a failed
            // mutation must not pass silently - the agent would walk
            // away believing the ticket is tagged.
            <div role="alert" className="mt-1 text-xs text-red-600">
              Could not update tags. Try again.
            </div>
          )}
          {open && (
            <ul
              id={listboxId}
              role="listbox"
              data-testid="tags-combobox-listbox"
              className="absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg"
              // Stop the input's blur from closing the dropdown before
              // a row click lands; commitSuggestion re-focuses cleanly.
              onMouseDown={(e) => {
                if (blurTimerRef.current !== null) {
                  window.clearTimeout(blurTimerRef.current);
                  blurTimerRef.current = null;
                }
                e.preventDefault();
              }}
            >
              {items.length === 0 && (
                <li className="px-3 py-2 text-xs text-neutral-400">
                  {needle
                    ? "Press Enter to create this tag."
                    : "Type to create a tag."}
                </li>
              )}
              {items.map((tag, i) => {
                const isActive = i === highlightIndex;
                return (
                  <li
                    key={tag.id}
                    id={`${listboxId}-${i}`}
                    role="option"
                    aria-selected={isActive}
                    data-testid="tags-combobox-option"
                    onMouseEnter={() => setHighlightIndex(i)}
                    onClick={() => commitSuggestion(tag)}
                    className={`cursor-pointer truncate px-3 py-1.5 text-sm ${
                      isActive
                        ? "bg-primary-50 text-primary-900"
                        : "text-neutral-800"
                    }`}
                  >
                    {tag.name}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default TagsEditor;
